import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import Divider from "@material-ui/core/Divider";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import SaveIcon from "@material-ui/icons/Save";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import PaymentIcon from "@material-ui/icons/Payment";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import SentimentVeryDissatisfiedIcon from "@material-ui/icons/SentimentVeryDissatisfied";

import CircularProgress from "@material-ui/core/CircularProgress";

import api from "../../services/api";
import { getBackendUrl } from "../../config";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  card: {
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    border: "1px solid rgba(255, 255, 255, 0.12)",
    background: theme.palette.type === "dark" ? "#161B22" : "#FFFFFF",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
  },
  headerBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  webhookUrlBox: {
    display: "flex",
    alignItems: "center",
    background: theme.palette.type === "dark" ? "#0D1117" : "#F6F8FA",
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    border: "1px solid rgba(0, 0, 0, 0.1)",
    fontFamily: "monospace",
    fontSize: "0.9rem",
    marginTop: theme.spacing(1),
  },
  tagChip: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: "0.75rem",
    background: "rgba(16, 185, 129, 0.15)",
    color: "#10B981",
    fontWeight: "bold",
    marginRight: 6,
    marginBottom: 6,
    fontFamily: "monospace",
    cursor: "pointer",
  }
}));

const Kiwify = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [whatsapps, setWhatsapps] = useState([]);
  const [copied, setCopied] = useState(false);

  // Estado das configurações
  const [config, setConfig] = useState({
    enabled: true,
    whatsappId: "",
    whatsappName: "primenutra",
    secretKey: "",
    adminPhone: "",
    notifyAdmin: true,
    abandonedCartEnabled: true,
    abandonedCartMessage: "",
    pixCreatedEnabled: true,
    pixCreatedMessage: "",
    orderRejectedEnabled: true,
    orderRejectedMessage: "",
    orderApprovedEnabled: true,
    orderApprovedMessage: ""
  });

  const backendUrl = (getBackendUrl() || "").replace(/\/+$/, "");
  const webhookUrl = `${backendUrl}/kiwify/webhook`;

  useEffect(() => {
    fetchWhatsapps();
    fetchConfig();
  }, []);

  const fetchWhatsapps = async () => {
    try {
      const { data } = await api.get("/whatsapp");
      setWhatsapps(data || []);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/kiwify/config");
      if (data) {
        setConfig(data);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await api.post("/kiwify/config", config);
      toast.success("Configurações da Kiwify salvas com sucesso!");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    toast.info("URL do Webhook copiada para a área de transferência!");
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Integração Kiwify (Recuperação & Boas-Vindas)</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            style={{ borderRadius: 8, fontWeight: "bold", padding: "8px 24px" }}
          >
            {loading ? "Salvando..." : "Salvar Configurações"}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={3}>
          {/* BLOCO 1: WEBHOOK & CONEXÃO */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card} variant="outlined">
              <CardHeader
                title="🔗 Webhook Oficial da Kiwify"
                subheader="Cadastre esta URL na Kiwify (Configurações > Webhooks)"
              />
              <Divider />
              <CardContent>
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                  URL DE DESTINO DO WEBHOOK:
                </Typography>
                <div className={classes.webhookUrlBox}>
                  <span style={{ flex: 1, wordBreak: "break-all" }}>{webhookUrl}</span>
                  <Tooltip title={copied ? "Copiado!" : "Copiar URL"}>
                    <IconButton size="small" onClick={handleCopyWebhook} color={copied ? "primary" : "default"}>
                      {copied ? <CheckCircleIcon fontSize="small" /> : <FileCopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </div>

                <div style={{ marginTop: 16 }}>
                  <TextField
                    fullWidth
                    label="Token do Webhook Kiwify (Secret Key / Assinatura)"
                    variant="outlined"
                    size="small"
                    placeholder="Cole aqui o token de validação gerado na Kiwify"
                    value={config.secretKey || ""}
                    onChange={(e) => setConfig({ ...config, secretKey: e.target.value })}
                    helperText="Opcional. Se informado, valida a assinatura criptográfica HMAC da requisição."
                  />
                </div>
              </CardContent>
            </Card>
          </Grid>

          {/* BLOCO 2: DISPARADOR & ADMIN */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card} variant="outlined">
              <CardHeader
                title="⚙️ Conexão de Disparo & Notificações"
                subheader="Escolha por qual número as mensagens serão enviadas"
              />
              <Divider />
              <CardContent>
                <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 16 }}>
                  <InputLabel id="select-whatsapp-label">Conexão WhatsApp de Envio</InputLabel>
                  <Select
                    labelId="select-whatsapp-label"
                    value={config.whatsappId || ""}
                    onChange={(e) => {
                      const selectedWhats = whatsapps.find((w) => w.id === e.target.value);
                      setConfig({
                        ...config,
                        whatsappId: e.target.value,
                        whatsappName: selectedWhats ? selectedWhats.name : ""
                      });
                    }}
                    label="Conexão WhatsApp de Envio"
                  >
                    {whatsapps.map((w) => (
                      <MenuItem key={w.id} value={w.id}>
                        {w.name} {w.name === "primenutra" ? "⭐ (Recomendada)" : ""} ({w.status})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="WhatsApp Pessoal do Administrador (com DDD)"
                  variant="outlined"
                  size="small"
                  placeholder="Ex: 5585981497934"
                  value={config.adminPhone || ""}
                  onChange={(e) => setConfig({ ...config, adminPhone: e.target.value })}
                  helperText="Recebe um alerta instantâneo a cada carrinho abandonado ou compra aprovada."
                  style={{ marginBottom: 12 }}
                />

                <div style={{ display: "flex", gap: 16 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.enabled}
                        onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                        color="primary"
                      />
                    }
                    label="Ativação Geral"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.notifyAdmin}
                        onChange={(e) => setConfig({ ...config, notifyAdmin: e.target.checked })}
                        color="primary"
                      />
                    }
                    label="Avisar Administrador"
                  />
                </div>
              </CardContent>
            </Card>
          </Grid>

          {/* BLOCO 3: AS 4 MENSAGENS ESTRATÉGICAS */}

          {/* CARD 1: CARRINHO ABANDONADO */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card} variant="outlined">
              <CardHeader
                avatar={<SentimentVeryDissatisfiedIcon style={{ color: "#F59E0B" }} />}
                title="1. Carrinho Abandonado"
                action={
                  <Switch
                    checked={config.abandonedCartEnabled}
                    onChange={(e) => setConfig({ ...config, abandonedCartEnabled: e.target.checked })}
                    color="primary"
                  />
                }
                subheader="Lead digitou dados no checkout mas não concluiu a compra"
              />
              <Divider />
              <CardContent>
                <div style={{ marginBottom: 10 }}>
                  <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                    Variáveis disponíveis:
                  </Typography>
                  <span className={classes.tagChip}>&#123;primeiro_nome&#125;</span>
                  <span className={classes.tagChip}>&#123;nome_completo&#125;</span>
                  <span className={classes.tagChip}>&#123;produto&#125;</span>
                  <span className={classes.tagChip}>&#123;checkout_url&#125;</span>
                </div>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  variant="outlined"
                  value={config.abandonedCartMessage}
                  onChange={(e) => setConfig({ ...config, abandonedCartMessage: e.target.value })}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* CARD 2: PIX GERADO */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card} variant="outlined">
              <CardHeader
                avatar={<PaymentIcon style={{ color: "#10B981" }} />}
                title="2. Pix Gerado (Aguardando Pagamento)"
                action={
                  <Switch
                    checked={config.pixCreatedEnabled}
                    onChange={(e) => setConfig({ ...config, pixCreatedEnabled: e.target.checked })}
                    color="primary"
                  />
                }
                subheader="Envia o código Pix Copia e Cola facilitado no WhatsApp"
              />
              <Divider />
              <CardContent>
                <div style={{ marginBottom: 10 }}>
                  <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                    Variáveis disponíveis:
                  </Typography>
                  <span className={classes.tagChip}>&#123;primeiro_nome&#125;</span>
                  <span className={classes.tagChip}>&#123;produto&#125;</span>
                  <span className={classes.tagChip}>&#123;pix_code&#125;</span>
                  <span className={classes.tagChip}>&#123;valor&#125;</span>
                </div>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  variant="outlined"
                  value={config.pixCreatedMessage}
                  onChange={(e) => setConfig({ ...config, pixCreatedMessage: e.target.value })}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* CARD 3: COMPRA RECUSADA */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card} variant="outlined">
              <CardHeader
                avatar={<ErrorOutlineIcon style={{ color: "#EF4444" }} />}
                title="3. Compra Recusada (Cartão Não Autorizado)"
                action={
                  <Switch
                    checked={config.orderRejectedEnabled}
                    onChange={(e) => setConfig({ ...config, orderRejectedEnabled: e.target.checked })}
                    color="primary"
                  />
                }
                subheader="Avisa a cliente com acolhimento e link para tentar outro método"
              />
              <Divider />
              <CardContent>
                <div style={{ marginBottom: 10 }}>
                  <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                    Variáveis disponíveis:
                  </Typography>
                  <span className={classes.tagChip}>&#123;primeiro_nome&#125;</span>
                  <span className={classes.tagChip}>&#123;produto&#125;</span>
                  <span className={classes.tagChip}>&#123;checkout_url&#125;</span>
                </div>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  variant="outlined"
                  value={config.orderRejectedMessage}
                  onChange={(e) => setConfig({ ...config, orderRejectedMessage: e.target.value })}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* CARD 4: COMPRA APROVADA */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card} variant="outlined">
              <CardHeader
                avatar={<ShoppingCartIcon style={{ color: "#3B82F6" }} />}
                title="4. Compra Aprovada (Boas-Vindas)"
                action={
                  <Switch
                    checked={config.orderApprovedEnabled}
                    onChange={(e) => setConfig({ ...config, orderApprovedEnabled: e.target.checked })}
                    color="primary"
                  />
                }
                subheader="Parabeniza a aluna e entrega o link de criação de senha"
              />
              <Divider />
              <CardContent>
                <div style={{ marginBottom: 10 }}>
                  <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                    Variáveis disponíveis:
                  </Typography>
                  <span className={classes.tagChip}>&#123;primeiro_nome&#125;</span>
                  <span className={classes.tagChip}>&#123;produto&#125;</span>
                  <span className={classes.tagChip}>&#123;access_url&#125;</span>
                  <span className={classes.tagChip}>&#123;email&#125;</span>
                </div>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  variant="outlined"
                  value={config.orderApprovedMessage}
                  onChange={(e) => setConfig({ ...config, orderApprovedMessage: e.target.value })}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, marginBottom: 24 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSave}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
              style={{ borderRadius: 8, fontWeight: "bold", padding: "12px 36px", fontSize: "1rem" }}
            >
              {loading ? "Salvando..." : "Salvar Configurações da Kiwify"}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default Kiwify;
